import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useHeroSlides, DEFAULT_HERO_SLIDES, HeroSlide } from "@/hooks/useHeroSlides";

export function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Fetch slides from database
  const { data: dbSlides, isLoading } = useHeroSlides();
  
  // Use DB slides if available, otherwise fallback to defaults
  const heroSlides = useMemo(() => {
    if (dbSlides && dbSlides.length > 0) {
      return dbSlides;
    }
    return DEFAULT_HERO_SLIDES;
  }, [dbSlides]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const goToSlide = (index: number) => setCurrentSlide(index);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);

  return (
    <section className="relative h-screen min-h-[700px] overflow-hidden bg-charcoal">
      <AnimatePresence mode="popLayout">
        {heroSlides[currentSlide] && (
            <motion.div
              key={heroSlides[currentSlide].id}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
              className="absolute inset-0 will-change-transform"
            >
              {/* Background Image with Cinematic Ken Burns */}
              <motion.div 
                className="absolute inset-0 will-change-transform"
                initial={{ scale: 1.08 }}
                animate={{ scale: 1 }}
                transition={{ duration: 8, ease: "linear" }}
              >
                <img
                  src={heroSlides[currentSlide].image}
                  alt={heroSlides[currentSlide].title}
                  className="w-full h-full object-cover grayscale-[20%] brightness-[0.85]"
                  loading="eager"
                />
              </motion.div>
              
              {/* Sophisticated Gradient Veil */}
              <div 
                className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/60 to-transparent"
              />
              <div 
                className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-transparent to-charcoal/30 md:from-charcoal/60"
              />
              {/* Mobile-specific overlay for better text readability */}
              <div 
                className="absolute inset-0 bg-charcoal/30 md:hidden"
              />

              {/* Content Integration */}
              <div className="relative z-10 container-luxury h-full flex flex-col justify-center px-6 md:px-12">
                <div className="max-w-4xl">
                  {/* Decorative Sparkle */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="flex items-center gap-3 mb-4 md:mb-6"
                  >
                    <div className="h-[1px] w-8 md:w-12 bg-gold/50" />
                    <Sparkles size={16} className="text-gold animate-pulse" />
                    <span className="text-gold text-[10px] uppercase tracking-[0.5em] font-bold">Leestone Heritage</span>
                  </motion.div>

                  {/* Subtitle with reveal */}
                  <div className="overflow-hidden mb-2 md:mb-4">
                    <motion.p 
                      className="text-white/70 text-[10px] sm:text-xs md:text-lg tracking-[0.2em] sm:tracking-[0.4em] uppercase font-medium italic"
                      initial={{ y: "100%" }}
                      animate={{ y: 0 }}
                      transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
                    >
                      {heroSlides[currentSlide].subtitle}
                    </motion.p>
                  </div>
                  
                  {/* Majestic Title */}
                  <div className="overflow-hidden mb-4 md:mb-8 pb-4">
                    <motion.h2 
                      className="font-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl xl:text-9xl text-white leading-none tracking-tighter"
                      initial={{ y: "100%" }}
                      animate={{ y: 0 }}
                      transition={{ duration: 1, delay: 0.8, ease: "easeOut" }}
                    >
                      {heroSlides[currentSlide].title.split(' ').map((word, i) => (
                        <span key={i} className={cn("block", i === 1 && "italic pl-4 md:pl-16 text-gold/90 pr-4")}>
                          {word}
                        </span>
                      ))}
                    </motion.h2>
                  </div>
                  
                  {/* Refined Description */}
                  <motion.p 
                    className="text-white/80 text-sm md:text-xl mb-6 md:mb-12 leading-relaxed max-w-sm md:max-w-xl font-light italic border-l border-gold/20 pl-4 md:pl-8"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                  >
                    {heroSlides[currentSlide].description}
                  </motion.p>
                  
                  {/* High-End Action */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.4 }}
                  >
                    <Link 
                      to={heroSlides[currentSlide].cta_link} 
                      className="group relative inline-flex items-center gap-2 sm:gap-4 md:gap-6 bg-white text-charcoal px-5 sm:px-8 md:px-12 py-3 sm:py-4 md:py-6 text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] font-bold overflow-hidden transition-all duration-700 hover:text-white"
                    >
                      <div className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-700" />
                      <span className="relative z-10">{heroSlides[currentSlide].cta_text}</span>
                      <ArrowRight size={16} className="relative z-10 group-hover:translate-x-2 transition-transform duration-500" />
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation Controls - Minimal Ivory */}
      <div className="absolute right-6 md:right-12 bottom-20 md:bottom-24 flex flex-col gap-4 z-20">
        <button
          onClick={prevSlide}
          className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center border border-white/10 text-white hover:border-gold hover:text-gold transition-all duration-500 rounded-full backdrop-blur-sm"
          aria-label="Previous Heritage Slide"
        >
          <ChevronLeft size={20} className="md:w-6 md:h-6" strokeWidth={1} />
        </button>
        <button
          onClick={nextSlide}
          className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center border border-white/10 text-white hover:border-gold hover:text-gold transition-all duration-500 rounded-full backdrop-blur-sm"
          aria-label="Next Heritage Slide"
        >
          <ChevronRight size={20} className="md:w-6 md:h-6" strokeWidth={1} />
        </button>
      </div>

      {/* Progress Architecture */}
      <div className="absolute bottom-8 md:bottom-12 left-6 md:left-12 flex items-end gap-16 z-20">
        <div className="flex gap-4">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className="group py-4"
              aria-label={`Archive Slide ${index + 1}`}
            >
              <div className="relative w-8 md:w-12 h-px bg-white/20 overflow-hidden">
                <motion.div 
                  className="absolute inset-y-0 left-0 bg-gold"
                  animate={{ width: index === currentSlide ? "100%" : "0%" }}
                  transition={{ duration: index === currentSlide ? 8 : 0.2, ease: "linear" }}
                />
              </div>
            </button>
          ))}
        </div>
        
        <div className="hidden md:flex items-center gap-4 text-white/40 text-[10px] uppercase tracking-[0.4em] font-bold">
          <span className="text-white text-base">0{currentSlide + 1}</span>
          <div className="w-12 h-px bg-gold/30" />
          <span>0{heroSlides.length}</span>
        </div>
      </div>
    </section>
  );
}